import { MicroSoftConfig, getTenantId, getClientId } from './MicrosoftConfig';

const tenantId = getTenantId(false);
const clientId = getClientId(false);

// PKCE helpers
async function sha256(input) {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(hashBuffer);
}
function base64UrlEncode(bytes) {
  let str = '';
  bytes.forEach((b) => (str += String.fromCharCode(b)));
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

async function createPkcePair() {
  const random = crypto.getRandomValues(new Uint8Array(32));
  const verifier = base64UrlEncode(random).slice(0, 64); // 43–128 chars
  const challenge = base64UrlEncode(await sha256(verifier));
  return { verifier, challenge };
}

const MicrosoftAuthService = {
  async getAuthorizationUrl() {
    const redirect = `${window.location.origin}/callbackEntra`;
    const authorizeEndpoint = `${MicroSoftConfig.loginUrl}${tenantId}/oauth2/v2.0/authorize`;
    const scope = encodeURIComponent(
      'openid profile email offline_access User.Read'
    );

    const { verifier, challenge } = await createPkcePair();
    sessionStorage.setItem('ms_pkce_verifier', verifier);

    const params = [
      `client_id=${encodeURIComponent(clientId)}`,
      'response_type=code',
      `redirect_uri=${encodeURIComponent(redirect)}`,
      `scope=${scope}`,
      'response_mode=query',
      'sso_reload=true',
      `code_challenge=${challenge}`,
      'code_challenge_method=S256',
      `state=${encodeURIComponent(Math.random().toString(36).slice(2))}`,
    ].join('&');

    return `${authorizeEndpoint}?${params}`;
  },

  async getToken(code) {
    const redirect = `${window.location.origin}/callbackEntra`;
    const tokenEndpoint = `${MicroSoftConfig.loginUrl}${tenantId}/oauth2/v2.0/token`;
    const code_verifier = sessionStorage.getItem('ms_pkce_verifier');
    if (!code_verifier)
      throw new Error('Missing PKCE verifier. Start login again.');

    const body = new URLSearchParams({
      client_id: clientId,
      grant_type: 'authorization_code',
      code,
      redirect_uri: redirect,
      code_verifier,
    });

    const res = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });

    if (!res.ok) {
      const err = await res.text();
      throw new Error(`Microsoft token error: ${err}`);
    }
    return res.json();
  },

  async getUserInfo(accessToken) {
    const res = await fetch(
      `${MicroSoftConfig.graphUrl}me?$select=givenName,surname,displayName,mail,userPrincipalName`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (!res.ok) {
      const err = await res.text();
      throw new Error(`Microsoft Graph /me error: ${err}`);
    }
    const me = await res.json();
    return {
      firstName: me.givenName || '',
      lastName: me.surname || '',
      email: me.mail || me.userPrincipalName || '',
    };
  },
};

export default MicrosoftAuthService;
